import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import React from 'react'
import { safeSessionStorage } from '@/utility/safeStorage'

// Member data interface for type safety
interface MemberData {
  id?: number
  email?: string
  name?: string
}

// Bugsnag metadata type - allows string, number, boolean, or nested objects
type BugsnagMetadataValue = string | number | boolean | null | undefined | BugsnagMetadata
interface BugsnagMetadata {
  [key: string]: BugsnagMetadataValue | BugsnagMetadataValue[]
}

// Bugsnag configuration options
const bugsnagConfig = {
  apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || '',
  releaseStage: process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.NODE_ENV || 'development',
  enabledReleaseStages: ['production', 'staging'],
  collectUserIp: false,
  maxBreadcrumbs: 40,
  metadata: {
    app: {
      version:
        process.env.NEXT_PUBLIC_BUGSNAG_APP_VERSION ||
        process.env.GIT_COMMIT_SHA ||
        process.env.NEXT_PUBLIC_GIT_COMMIT_SHA ||
        '1.0.0',
      buildTime: new Date().toISOString(),
    },
  },
  onError: (event: any) => {
    // Add user context if available
    const member = safeSessionStorage.getJSON<MemberData | null>('member', null)
    if (member) {
      event.setUser(member.id?.toString(), member.email, member.name)
    }

    // Add custom metadata
    event.addMetadata('navigation', {
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      search: typeof window !== 'undefined' ? window.location.search : 'unknown',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    })

    // Filter out specific errors if needed
    const ignoredErrors = [
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ]

    if (ignoredErrors.some((ignored) => event.errors[0]?.errorMessage?.includes(ignored))) {
      return false // Don't send this error to Bugsnag
    }

    return true // Send error to Bugsnag
  },
}

// Initialize Bugsnag for client-side
let bugsnagClient: any = null

export const initBugsnag = () => {
  if (typeof window === 'undefined') {
    return null
  }

  if (!process.env.NEXT_PUBLIC_BUGSNAG_API_KEY) {
    return null
  }

  if (!bugsnagClient) {
    Bugsnag.start({
      ...bugsnagConfig,
      plugins: [new BugsnagPluginReact(React)],
    })
    bugsnagClient = Bugsnag
  }

  return bugsnagClient
}

// Get the Bugsnag client instance
export const getBugsnag = () => {
  if (typeof window === 'undefined') {
    return null
  }
  return bugsnagClient || initBugsnag()
}

// Bugsnag React Error Boundary
export const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const bugsnag = getBugsnag()
  if (!bugsnag || !bugsnag.getPlugin('react')) {
    // No BugSnag available, just render children without error boundary
    return React.createElement(React.Fragment, null, children)
  }

  const ErrorBoundaryComponent = bugsnag.getPlugin('react').createErrorBoundary(React)

  return React.createElement(
    ErrorBoundaryComponent,
    {
      FallbackComponent: ({ error, info, clearError }: any) =>
        React.createElement(
          'div',
          {
            className: 'error-boundary-container',
            style: { padding: '20px', textAlign: 'center' },
          },
          React.createElement('h2', null, 'エラーが発生しました'),
          React.createElement('p', null, '申し訳ございません。予期しないエラーが発生しました。'),
          React.createElement(
            'details',
            { style: { margin: '20px 0' } },
            React.createElement('summary', null, 'エラー詳細'),
            React.createElement(
              'pre',
              { style: { textAlign: 'left', overflow: 'auto' } },
              error?.toString(),
              info?.componentStack,
            ),
          ),
          React.createElement('button', { onClick: clearError, className: 'buttonBase' }, '再試行'),
        ),
    },
    children,
  )
}

// Helper function to notify Bugsnag about handled errors
export const notifyError = (error: Error | string, metadata?: BugsnagMetadata) => {
  const bugsnag = getBugsnag()
  if (!bugsnag) return

  if (typeof error === 'string') {
    bugsnag.notify(new Error(error), (event: any) => {
      if (metadata) {
        Object.keys(metadata).forEach((key) => {
          event.addMetadata(key, metadata[key])
        })
      }
    })
  } else {
    bugsnag.notify(error, (event: any) => {
      if (metadata) {
        Object.keys(metadata).forEach((key) => {
          event.addMetadata(key, metadata[key])
        })
      }
    })
  }
}

// Helper function to leave breadcrumbs
export const leaveBreadcrumb = (
  message: string,
  metadata?: BugsnagMetadata,
  type: 'navigation' | 'state' | 'error' | 'log' | 'user' = 'log',
) => {
  const bugsnag = getBugsnag()
  if (!bugsnag) return

  bugsnag.leaveBreadcrumb(message, metadata, type)
}

// Helper function to add metadata to all future errors
export const addMetadata = (section: string, data: BugsnagMetadata) => {
  const bugsnag = getBugsnag()
  if (!bugsnag) return

  bugsnag.addMetadata(section, data)
}

// Helper function to set user context
export const setUser = (id?: string, email?: string, name?: string) => {
  const bugsnag = getBugsnag()
  if (!bugsnag) return

  bugsnag.setUser(id, email, name)
}
